import React from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { useNavigate } from 'react-router-dom';
import { VendorAppNotification } from '@/services/notifications';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import type { ProfessionalReviewTargetI } from '@/containers/Reviews/types';
import {
  formatNotificationAmount,
  NotificationRow,
  NotificationRowContent,
  NotificationRowType
} from './NotificationRow';

interface ApiNotificationProps {
  notification: VendorAppNotification;
  onMarkAsRead: (notificationId: number) => void;
  onArchive: (notificationId: number) => void;
  onClick?: (bookingId: number) => void;
  /** Abre la reseña de su turno (aviso del profesional del equipo, `review_for_professional`). */
  onOpenProfessionalReview?: (target: ProfessionalReviewTargetI) => void;
  index?: number;
  isLast?: boolean;
  variant?: 'dropdown' | 'sheet';
}

/** « ★★★★☆» para sumar al título; vacío sin estrellas. */
const starsSuffix = (value: number | null | undefined): string => {
  const rating = Math.max(0, Math.min(5, Math.round(value ?? 0)));
  return rating > 0 ? ` ${'★'.repeat(rating)}${'☆'.repeat(5 - rating)}` : '';
};

/** «Sofía dejó una reseña ★★★★☆» / «Sofía te dejó una nota privada». */
export const reviewNotificationTitle = (notification: VendorAppNotification): string => {
  const who = notification.data.client_name || 'Un cliente';
  if (notification.type === 'review_private_note') return `${who} te dejó una nota privada`;
  return `${who} dejó una reseña${starsSuffix(notification.data.rating)}`;
};

/**
 * Al profesional del equipo, una reseña de un turno suyo: «Ana te dejó una reseña ★★★★★» o
 * «Ana te dejó una reseña en Google ★★★★☆». El nombre falta si no puede ver los de los clientes.
 * Abajo, lo que escribió (solo Ágora) o el servicio del turno.
 */
export const professionalReviewNotificationContent = (
  notification: VendorAppNotification
): Pick<NotificationRowContent, 'title' | 'subtitle'> => {
  const { customerName, source, rating, excerpt, service_name } = notification.data;
  const who = customerName?.trim() || 'Un cliente';
  const where = source === 'google' ? ' en Google' : '';
  return {
    title: `${who} te dejó una reseña${where}${starsSuffix(rating)}`,
    subtitle: (source !== 'google' && excerpt) || service_name || 'Tocá para leerla'
  };
};

/** Qué reseña abre el aviso del profesional; `null` si al aviso le falta el id. */
export const professionalReviewTargetOf = (
  notification: VendorAppNotification
): ProfessionalReviewTargetI | null => {
  const { source, review_id, google_review_id } = notification.data;
  if (source === 'google') return google_review_id ? { source: 'google', id: google_review_id } : null;
  return review_id ? { source: 'agora', id: review_id } : null;
};

/** El extracto de lo que escribió; si no escribió nada, la visita a la que califica. */
export const reviewNotificationSubtitle = (notification: VendorAppNotification): string => {
  const { excerpt, service_name, staff_name } = notification.data;
  if (excerpt) return excerpt;
  if (service_name) return `${service_name}${staff_name ? ` · con ${staff_name}` : ''}`;
  return 'Tocá para leerla';
};

/** « · atendió Juli» al final, cuando la reseña de Google llegó por nuestro pedido. */
const withAttendedBy = (text: string, notification: VendorAppNotification): string => {
  const staff = notification.data.staff_first_name?.trim();
  return staff ? `${text} · atendió ${staff}` : text;
};

/**
 * «Nueva reseña en Google» + «4 de 5 estrellas». Google no deja guardar el nombre ni el texto. Si
 * llegó por nuestro pedido de reseña, quién atendió es dato nuestro: «5 de 5 estrellas · atendió Juli».
 */
export const googleReviewNotificationSubtitle = (notification: VendorAppNotification): string => {
  const rating = notification.data.rating;
  if (rating === null || rating === undefined || !Number.isFinite(rating) || rating <= 0)
    return withAttendedBy('Sin estrellas', notification);
  const stars = Math.max(1, Math.min(5, Math.round(rating)));
  return withAttendedBy(`${stars} de 5 ${stars === 1 ? 'estrella' : 'estrellas'}`, notification);
};

const validStars = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.max(1, Math.min(5, Math.round(value)))
    : null;

/**
 * Una reseña de Google que el cliente editó y le cambió las estrellas (`previous_rating`):
 * «Cambió una reseña en Google» · «De 3 a 5 estrellas». Sin `previous_rating` es una nueva.
 */
export const googleReviewNotificationContent = (
  notification: VendorAppNotification
): Pick<NotificationRowContent, 'title' | 'subtitle'> => {
  const previous = validStars(notification.data.previous_rating);
  if (previous === null)
    return { title: 'Nueva reseña en Google', subtitle: googleReviewNotificationSubtitle(notification) };
  const now = validStars(notification.data.rating);
  return {
    title: 'Cambió una reseña en Google',
    subtitle: withAttendedBy(
      now === null
        ? `Antes tenía ${previous} ${previous === 1 ? 'estrella' : 'estrellas'}`
        : `De ${previous} a ${now} ${now === 1 ? 'estrella' : 'estrellas'}`,
      notification
    )
  };
};

const pointsFormat = new Intl.NumberFormat('es-AR');

/**
 * Premios, con la forma de los avisos de reservas: quién, qué premio y qué turno.
 * «Lucía Pérez llegó a un premio» · «Juntó 1.000 puntos: 10% en cada turno».
 */
export const premioNotificationContent = (notification: VendorAppNotification): NotificationRowContent => {
  const { client_name, premio_title, points, amount, start_at } = notification.data;
  const who = client_name || 'Un cliente';
  const discount = amount ? `−$${formatNotificationAmount(amount)}` : '';
  if (notification.type === 'premio_won')
    return {
      title: `${who} llegó a un premio`,
      subtitle: [points ? `Juntó ${pointsFormat.format(points)} puntos` : '', premio_title]
        .filter(Boolean)
        .join(': '),
      createdAt: notification.created_at
    };
  if (notification.type === 'premio_used')
    return {
      title: `${who} usó su premio`,
      subtitle: [premio_title, discount].filter(Boolean).join(' · '),
      createdAt: notification.created_at
    };
  const when = start_at ? dayjs(start_at).locale('es').format('ddd D MMM · HH:mm') : '';
  return {
    title: `${who} tiene premio mañana`,
    subtitle: [premio_title, when, discount].filter(Boolean).join(' · '),
    createdAt: notification.created_at
  };
};

/** Fichaje del equipo: quién no llegó antes de su primer turno, o el PIN de recepción bloqueado. */
const attendanceNotificationContent = (notification: VendorAppNotification): NotificationRowContent =>
  notification.type === 'attendance_missing'
    ? {
        title: `${notification.data.name || 'Alguien del equipo'} todavía no llegó`,
        subtitle: notification.data.first_booking_at
          ? `Tiene un turno a las ${notification.data.first_booking_at}.`
          : 'Tiene turnos hoy y no marcó su llegada.',
        createdAt: notification.created_at
      }
    : {
        title: 'PIN equivocado tres veces',
        subtitle: `La pantalla de fichaje${
          notification.data.venue_name ? ` de ${notification.data.venue_name}` : ''
        } bloqueó el PIN por un minuto.`,
        createdAt: notification.created_at
      };

/**
 * Mercado Pago por profesional. Al dueño: «Lucía desconectó su cuenta» (o Mercado Pago dejó de
 * aceptarla); al profesional: el negocio desconectó la suya. En los dos, sus turnos vuelven a la
 * cuenta del negocio.
 */
export const mpProfessionalNotificationContent = (
  notification: VendorAppNotification
): NotificationRowContent => {
  const { professional_name, business_name, reason } = notification.data;
  const who = professional_name || 'Un profesional';
  const business = business_name ? ` (${business_name})` : '';
  if (notification.type === 'mp_own_account_disconnected')
    return {
      title: `${business_name || 'El negocio'} desconectó tu cuenta de Mercado Pago`,
      subtitle: 'Desde ahora, tus turnos se cobran con la cuenta de Mercado Pago del negocio.',
      createdAt: notification.created_at
    };
  if (reason === 'mercado_pago')
    return {
      title: `Mercado Pago dejó de aceptar la cuenta de ${who}`,
      subtitle: `Mientras tanto, sus turnos se cobran con la cuenta del negocio${business}. Pasale el enlace para que la vuelva a conectar.`,
      createdAt: notification.created_at
    };
  return {
    title: `${who} desconectó su cuenta`,
    subtitle: `Desde ahora, sus turnos se cobran con la cuenta de Mercado Pago del negocio${business}.`,
    createdAt: notification.created_at
  };
};

const standaloneTypes = new Set([
  'ig_screenshots_ready',
  'community_question',
  'community_report',
  'community_update_posted',
  'community_member_joined',
  'review_received',
  'review_private_note',
  'google_review_received',
  'review_for_professional',
  'product_low_stock',
  'gift_card_purchased',
  'attendance_missing',
  'attendance_pin_locked',
  'premio_won',
  'premio_used',
  'premio_tomorrow',
  'mp_professional_disconnected',
  'mp_own_account_disconnected'
]);

/** Shared by the bell and the full center, including their empty states. */
export const isApiNotificationVisible = (
  notification: VendorAppNotification,
  flags: {
    COMMUNITY: boolean;
    REVIEWS: boolean;
    TEAM_ATTENDANCE: boolean;
    PREMIOS: boolean;
    MP_POR_PROFESIONAL: boolean;
  }
): boolean => {
  if (notification.type.startsWith('mp_') && !flags.MP_POR_PROFESIONAL) return false;
  if (notification.type.startsWith('community_') && !flags.COMMUNITY) return false;
  if (notification.type.startsWith('premio_') && !flags.PREMIOS) return false;
  if (notification.type.startsWith('attendance_') && !flags.TEAM_ATTENDANCE) return false;
  if (
    ['review_received', 'review_private_note', 'google_review_received', 'review_for_professional'].includes(
      notification.type
    ) &&
    !flags.REVIEWS
  )
    return false;
  // Tipos retirados (pulse, daily_brief, wrapped, wa_human_handoff) y desconocidos no tienen
  // destino: las filas viejas que quedan en la base no se muestran.
  return (
    standaloneTypes.has(notification.type) ||
    (notification.type.startsWith('booking_') && !!notification.data.bookingId)
  );
};

export const ApiNotification: React.FC<ApiNotificationProps> = ({
  notification,
  onMarkAsRead,
  onArchive,
  onClick,
  onOpenProfessionalReview,
  index,
  isLast,
  variant
}) => {
  const isUnread = !notification.read;
  const navigate = useNavigate();
  const isCommunity =
    notification.type === 'community_question' ||
    notification.type === 'community_report' ||
    notification.type === 'community_update_posted' ||
    notification.type === 'community_member_joined';
  const isReview = notification.type === 'review_received' || notification.type === 'review_private_note';
  const isGoogleReview = notification.type === 'google_review_received';
  const isProfessionalReview = notification.type === 'review_for_professional';
  const isLowStock = notification.type === 'product_low_stock';
  const isGiftCard = notification.type === 'gift_card_purchased';
  const isAttendance =
    notification.type === 'attendance_missing' || notification.type === 'attendance_pin_locked';
  const isPremio = notification.type.startsWith('premio_');
  const isMpProfessional =
    notification.type === 'mp_professional_disconnected' ||
    notification.type === 'mp_own_account_disconnected';
  const flags = useFeatureFlags();
  if (!isApiNotificationVisible(notification, flags)) return null;

  const handleClick = () => {
    if (isUnread) onMarkAsRead(notification.id);
    if (notification.type === 'ig_screenshots_ready') {
      navigate('/contenido-ig/guia');
      return;
    }
    if (isCommunity) {
      // El payload traía post_id/report_id desde siempre y el deep-link del
      // container también existía: solo faltaba armar la URL.
      const data = notification.data as {
        post_id?: number;
        space_id?: number;
        report_id?: number;
      };
      if (notification.type === 'community_member_joined') {
        navigate('/comunidad?tab=personas');
      } else if (notification.type === 'community_report') {
        navigate(`/comunidad?tab=reportes${data.report_id ? `&report=${data.report_id}` : ''}`);
      } else {
        const params = new URLSearchParams({ tab: 'publicaciones' });
        if (data.post_id) params.set('post', String(data.post_id));
        if (data.space_id) params.set('space', String(data.space_id));
        navigate(`/comunidad?${params.toString()}`);
      }
      return;
    }
    if (isReview) {
      // El inbox abre el detalle de esa reseña y la marca leída.
      navigate(notification.data.review_id ? `/resenas?review=${notification.data.review_id}` : '/resenas');
      return;
    }
    if (isGoogleReview) {
      // La bandeja en Google, con esa reseña abierta para contestarla.
      const id = notification.data.google_review_id;
      navigate(id ? `/resenas?tab=google&review=google-${id}` : '/resenas?tab=google');
      return;
    }
    if (isProfessionalReview) {
      // El profesional no entra a /resenas (es del dueño): la reseña de su turno se lee en un diálogo.
      const target = professionalReviewTargetOf(notification);
      if (target) onOpenProfessionalReview?.(target);
      return;
    }
    if (isGiftCard) {
      navigate('/tarjetas-de-regalo');
      return;
    }
    if (isAttendance) {
      navigate('/equipo/asistencia');
      return;
    }
    if (isPremio) {
      // El que llegó a un premio abre los puntajes de Premios; el que lo usó o viene mañana, ese
      // turno. Sin turno (avisos viejos), también los puntajes.
      const bookingId = notification.data.booking_id;
      if (notification.type !== 'premio_won' && bookingId && onClick) onClick(bookingId);
      else navigate('/premios/clientes');
      return;
    }
    if (isMpProfessional) {
      // Al dueño, la ficha del profesional (sección Cobros); el profesional no tiene adónde ir.
      const id = notification.data.professional_vendor_id;
      if (notification.type === 'mp_professional_disconnected' && id) navigate(`/equipo/editar/${id}`);
      return;
    }
    if (isLowStock) {
      navigate(
        notification.data.productId ? `/producto/${notification.data.productId}/editar` : '/productos'
      );
      return;
    }
    if (onClick && notification.data.bookingId) onClick(notification.data.bookingId);
  };

  const baseContent: NotificationRowContent = isGiftCard
    ? {
        title: `${notification.data.customerName || 'Un cliente'} compró una tarjeta de regalo`,
        subtitle: [
          notification.data.amount != null ? `$${formatNotificationAmount(notification.data.amount)}` : '',
          notification.data.recipientName ? `para ${notification.data.recipientName}` : ''
        ]
          .filter(Boolean)
          .join(' · '),
        createdAt: notification.created_at
      }
    : isLowStock
      ? {
          title: `${notification.data.productName || 'Un producto'} ${
            notification.data.isOutOfStock || notification.data.remaining === 0
              ? 'se quedó sin stock'
              : 'tiene stock bajo'
          }`,
          subtitle:
            notification.data.remaining != null && notification.data.remaining > 0
              ? `Quedan ${notification.data.remaining} ${notification.data.remaining === 1 ? 'unidad' : 'unidades'}`
              : 'Reponé el stock para seguir vendiéndolo',
          createdAt: notification.created_at
        }
      : isGoogleReview
        ? {
            ...googleReviewNotificationContent(notification),
            createdAt: notification.created_at
          }
        : isReview
          ? {
              title: reviewNotificationTitle(notification),
              subtitle: reviewNotificationSubtitle(notification),
              createdAt: notification.created_at
            }
          : isCommunity
            ? {
                title:
                  notification.type === 'community_question'
                    ? `${notification.data.author_name || 'Un miembro'} preguntó en ${
                        notification.data.space_name || 'tu comunidad'
                      }`
                    : notification.type === 'community_member_joined'
                      ? `${notification.data.member_name || 'Alguien'} se sumó a ${
                          notification.data.membership_name || 'tu comunidad'
                        }`
                      : notification.type === 'community_update_posted'
                        ? `Tu ${
                            notification.data.asset_type === 'unique_event' ? 'capacitación' : 'curso'
                          } ya es novedad en tu comunidad`
                        : 'Reportaron contenido en tu comunidad',
                subtitle:
                  notification.type === 'community_question'
                    ? 'Tocá para responder'
                    : notification.type === 'community_member_joined'
                      ? [notification.data.amount_label, 'Tocá para ver a tus miembros']
                          .filter(Boolean)
                          .join(' · ')
                      : notification.type === 'community_update_posted'
                        ? 'Sumale unas palabras tuyas arriba de la card'
                        : 'Quedó oculto en forma preventiva — tocá para decidir',
                createdAt: notification.created_at
              }
            : notification.type === 'ig_screenshots_ready'
              ? {
                  title: 'Tus imágenes para Instagram están listas',
                  subtitle: 'Descargalas para usarlas en tus publicaciones',
                  createdAt: notification.created_at
                }
              : {
                  customerName: notification.data.customerName,
                  serviceName: notification.data.serviceName,
                  eventDate: notification.data.eventDate,
                  eventTime: notification.data.eventTime,
                  amount: notification.data.amount,
                  createdAt: notification.created_at,
                  oldEventDate: notification.data.oldEventDate,
                  oldEventTime: notification.data.oldEventTime,
                  newEventDate: notification.data.newEventDate,
                  newEventTime: notification.data.newEventTime,
                  warning:
                    flags.BOOKING_RESTRICTIONS && notification.type === 'booking_confirmed'
                      ? notification.data.restricted_contact_warning
                      : undefined
                };

  const content = isAttendance
    ? attendanceNotificationContent(notification)
    : isMpProfessional
      ? mpProfessionalNotificationContent(notification)
      : isPremio
        ? premioNotificationContent(notification)
        : isProfessionalReview
          ? { ...professionalReviewNotificationContent(notification), createdAt: notification.created_at }
          : baseContent;

  // La de Google lleva el ícono de Google, como el aviso del dueño; la de Ágora, la estrella.
  const rowType: NotificationRowType =
    isProfessionalReview && notification.data.source === 'google'
      ? 'google_review_received'
      : (notification.type as NotificationRowType);

  return (
    <NotificationRow
      type={rowType}
      unread={isUnread}
      stagger={index}
      isLast={isLast}
      variant={variant}
      onClick={handleClick}
      onArchive={() => onArchive(notification.id)}
      content={content}
    />
  );
};
